import {
  formatAmount,
  PROFILE_VIEW_SECTION,
  RAISE_STAGE_LABELS,
  STAGE_LABELS,
} from '@workspace/seedforge-shared'
import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'
import { Section } from '../components/section.js'
import type { DataRoomFundraising, DataRoomProfile } from '../types.js'

export interface HeaderSectionProps {
  profile: DataRoomProfile
  fundraising?: DataRoomFundraising
  /**
   * Investor-facing view. Missing facts are omitted rather than shown as "No
   * sector" — on a page a founder sends out, an empty-state placeholder is not
   * neutral, it advertises an unfinished profile in the slot reserved for
   * credibility. The founder's own editor keeps the prompts.
   */
  isPublic?: boolean
  /** On the right of the title, for the host app's own buttons (an investor's Follow). */
  actions?: ReactNode
}

export function HeaderSection({ profile, fundraising, isPublic, actions }: HeaderSectionProps) {
  const sector = profile.sectorLabel || profile.sector
  const stage = profile.stage ? (STAGE_LABELS[profile.stage] ?? profile.stage) : ''
  // Year only. The stored value is a date-picker default (Jan 1), and day-level
  // precision nobody entered reads as auto-generated — costly on a product
  // whose whole pitch is verified claims.
  const foundedYear = profile.founded ? new Date(profile.founded).getFullYear() : null

  const meta = [
    { key: 'sector', text: sector, tone: 'text-foreground/75 font-medium' },
    { key: 'stage', text: stage, tone: 'text-muted-foreground' },
    { key: 'location', text: profile.location, tone: 'text-muted-foreground' },
    {
      key: 'founded',
      text: foundedYear ? `Founded ${foundedYear}` : '',
      tone: 'text-muted-foreground/70 tabular-nums',
    },
  ].filter((item) => (isPublic ? !!item.text : true))

  const round = fundraising?.round
    ? (RAISE_STAGE_LABELS[fundraising.round] ?? fundraising.round)
    : ''
  const target = fundraising?.target ? formatAmount(fundraising.target) : ''

  return (
    <Section dataSection={PROFILE_VIEW_SECTION.HEADER}>
      <div className="flex flex-wrap items-start gap-4 lg:gap-5">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl border-2 border-[#f97316] bg-[#f97316]/10 text-lg font-bold text-[#f97316] lg:size-14 lg:text-xl">
          {profile.initials || '?'}
        </div>
        <div className="min-w-48 flex-1">
          {/* A title, not a list-item label. At the old size it sat 1.25x the
              body text, which is why the block read as a table row. */}
          <h1 className="text-balance break-words text-2xl font-bold leading-[1.15] tracking-[-0.02em] text-white lg:text-4xl lg:tracking-[-0.025em]">
            {profile.name || 'Your Startup'}
          </h1>

          {profile.raisingStatus && (round || target) && (
            /* Set as a line of type rather than a pill. The emerald chip sat
               beside the name at near-equal prominence and won — it is the
               coldest, highest-chroma thing on a warm page — amber sits in the
               same warm family as the rest of the palette, and carrying it on
               the words rather than a dot means the status is legible without a
               separate mark to decode. Brand orange, matching the CTA family. No pulse: animate-ping is notification
               semantics on a static fact, it ran unbounded with no
               reduced-motion escape, and it was the only moving thing on the
               page competing with the company name. */
            <p className="mt-2 text-sm font-medium text-[#f97316] lg:text-[15px]">
              Raising{round && ` ${round}`}
              {target && (
                <>
                  <span className="px-1.5 text-[#f97316]/40">·</span>
                  <span className="tabular-nums">{target}</span>
                  {/* "target" so the figure is not misread as raised-to-date. */}
                  <span className="text-[#f97316]/60"> target</span>
                </>
              )}
            </p>
          )}

          {meta.length > 0 && (
            /* Separators trail their item via `after:`, never lead the next one
               via `before:`. As sibling spans they orphaned onto a new line;
               `before:` reproduces exactly that, because the dot then wraps
               together with the item it precedes. Trailing is the only form
               that can't start a line. */
            <div className="mt-2.5 flex flex-wrap items-center text-xs lg:text-[13px]">
              {meta.map((item) => (
                <span
                  key={item.key}
                  className={cn(
                    item.text ? item.tone : 'text-muted-foreground/50',
                    "after:px-2 after:text-white/20 after:content-['·'] last:after:hidden"
                  )}
                >
                  {item.text || `No ${item.key}`}
                </span>
              ))}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-col items-end gap-1.5 max-sm:w-full max-sm:items-start">
            {actions}
          </div>
        )}
      </div>
    </Section>
  )
}
